import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useRoleList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refRolesTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'role',
      label: 'role name',
      sortable: true,
    },
    {
      key: 'modules',
      label: 'modules',
      sortable: true,
    },
    {
      key: 'departments',
      label: 'usable by department',
      sortable: false,
    },
    {
      key: 'redirectURL',
      label: 'Redirect URL',
      sortable: true,
    },
    {
      key: 'description',
      label: 'description',
      sortable: true,
    },
    {
      key: 'users',
      label: 'users',
      sortable: false,
    },
    {
      key: 'createdAt',
      label: 'created on',
      sortable: true,
    },
    {
      key: 'updatedAt',
      label: 'last updated',
      sortable: true,
    },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalRoles = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const sortBy = ref(route.query.sortBy ?? 'role')
  const isSortDirDesc = ref(route.query.sortDesc === 'true')
  const moduleFilter = ref([])
  if (Array.isArray(route.query.modules)) {
    moduleFilter.value = route.query.modules
  } else if (route.query.modules) {
    moduleFilter.value = [route.query.modules]
  }
  const departmentFilter = ref([])
  if (Array.isArray(route.query.department)) {
    departmentFilter.value = route.query.department
  } else if (route.query.department) {
    departmentFilter.value = [route.query.department]
  }
  const toDeletedID = ref(null)
  const currentDataLength = ref(0)
  let initialLoad = true

  const dataMeta = computed(() => {
    const localItemsCount = refRolesTable.value ? refRolesTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalRoles.value,
    }
  })

  const refetchData = () => {
    refRolesTable.value.refresh()
  }

  watch([searchQuery, moduleFilter, departmentFilter], () => {
    currentPage.value = 1
    refetchData()
  }, { deep: true })

  watch([currentPage], () => {
    refetchData()
  }, { deep: true })

  const fetchRoles = (ctx, callback) => {
    store
      .dispatch('app-roles/fetchRoles', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        modules: moduleFilter.value,
        department: departmentFilter.value,
      },
      { root: true })
      .then(response => {
        const { roles, total } = response.data
        currentDataLength.value = roles.length
        callback(roles)
        totalRoles.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              modules: moduleFilter.value,
              department: departmentFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const deleteRole = () => {
    store
      .dispatch('app-roles/deleteRole', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message ?? '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  return {
    fetchRoles,
    tableColumns,
    perPage,
    currentPage,
    totalRoles,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refRolesTable,

    refetchData,

    toDeletedID,
    deleteRole,
    currentDataLength,

    // Extra Filters
    moduleFilter,
    departmentFilter,
  }
}
